import { motion } from "framer-motion";
import { tokens } from "@/art/tokens";
import { useRun } from "@/state/run";
import { getScenarioUpcomingIntel } from "@/content/scenarioPresentation";

/**
 * HUD 상단 9주 달력. 과거 2주 + 현재 1주 + 미래 6주. 고정 이벤트가 있는 주차는 🏁 마커.
 * Uma 의 "달력에 빨간 깃발" 을 텍스트·라인 톤으로 옮김.
 *
 * <p>2026-04-22: 우측에 "다음 예고된 장면 카운트다운" 칩 병합.
 * frontier-vanguard 는 `getFrontierUpcomingIntel(week)` 에서 다음 주요 장면을
 * 계산해, "9주 뒤 · 벼랑끝 전투 · 주요 교전" 같은 칩으로 달력 옆에 박는다.
 * 플레이어가 "다음 뭐가 오는지" 를 달력 한 눈에 같이 읽게.
 */
export function CalendarStrip() {
    const run = useRun(s => s.run);
    if (!run) return null;

    const current = run.currentWeek;
    const windowStart = Math.max(1, current - 2);
    const windowEnd = Math.min(run.maxWeeks, current + 6);
    const weeks = Array.from({ length: windowEnd - windowStart + 1 }, (_, i) => windowStart + i);

    // 고정 이벤트 — RunDetailResponse 의 pendingEvents / eventLog 에서 SCENARIO_MILESTONE 추출.
    // 실제 백엔드의 scenario fixed events 는 turns 응답 nextFixedEvents 로 오지만,
    // 현재 HUD 는 일단 상시 노출된 pendingEvents 기반으로만 마킹. 미래 fixedEventTimeline 이 오면 교체.
    const milestoneWeeks = new Set<number>(
        run.eventLog
            .filter(e => e.eventType?.includes("MILESTONE"))
            .map(e => e.weekNo)
            .concat(
                run.pendingEvents
                    .filter(e => e.eventType?.includes("MILESTONE"))
                    .map(e => e.weekNo),
            ),
    );

    // 다음 예고된 장면 (frontier-vanguard 전용, 확장 시 다른 시나리오 lookup 추가).
    const upcoming = getScenarioUpcomingIntel(run.scenario.id, current);
    const upcomingDelta = upcoming ? upcoming.atWeek - current : null;
    // 미래 6주 창 안에 다음 사건이 들어오면 해당 주에 예고 마커도 표시.
    const upcomingInWindow = upcoming && upcoming.atWeek <= windowEnd && upcoming.atWeek > current
        ? upcoming.atWeek
        : null;

    return (
        <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: tokens.easing.decelerate }}
            className="flex items-center gap-xs rounded-m px-s py-xs relative overflow-hidden"
            style={{
                background: `linear-gradient(180deg, ${tokens.color.ink}D4, ${tokens.color.ink}BC)`,
                border: `1px solid ${tokens.color.brass}33`,
                backdropFilter: "blur(3px)",
                boxShadow: `inset 0 1px 0 ${tokens.color.brass}22, 0 6px 16px ${tokens.color.ink}66`,
            }}
        >
            {/* 좌측 "현재 주차" 배지 */}
            <div className="flex flex-col items-center shrink-0 px-s" style={{ borderRight: `1px solid ${tokens.color.parchmentDim}14` }}>
                <div
                    className="font-display leading-none"
                    style={{
                        color: tokens.color.ember,
                        fontSize: 17,
                        letterSpacing: "0.02em",
                        textShadow: `0 0 12px ${tokens.color.ember}66`,
                    }}
                >
                    {current}
                </div>
                <div
                    className="font-stat"
                    style={{
                        color: tokens.color.parchment,
                        fontSize: 11,
                        letterSpacing: "0.04em",
                        marginTop: 2,
                        textShadow: `${tokens.color.ink} 0px 2px 8px`,
                    }}
                >
                    / {run.maxWeeks}
                </div>
            </div>
            <div className="flex items-center gap-[2px]">
                {weeks.map(w => {
                    const isCurrent = w === current;
                    const hasFlag = milestoneWeeks.has(w);
                    const isUpcoming = w === upcomingInWindow;
                    const isPast = w < current;
                    return (
                        <div
                            key={w}
                            className="flex flex-col items-center"
                            style={{ width: 28 }}
                            title={isUpcoming ? upcoming?.title ?? undefined : undefined}
                        >
                            <div
                                className="font-stat"
                                style={{
                                    fontSize: isCurrent ? 12 : 10,
                                    fontWeight: isCurrent ? 700 : 400,
                                    color: isCurrent
                                        ? tokens.color.ember
                                        : isUpcoming
                                            ? tokens.color.brass
                                            : isPast
                                                ? tokens.color.parchment
                                                : tokens.color.parchment,
                                    textShadow: isCurrent ? `0 0 10px ${tokens.color.ember}` : "none",
                                    opacity: isPast ? 0.6 : 1,
                                    transition: "all 0.3s ease",
                                }}
                            >
                                {w}
                            </div>
                            <motion.div
                                className="mt-[2px] w-full rounded-pill"
                                animate={isCurrent ? { opacity: [0.9, 1, 0.9] } : { opacity: 1 }}
                                transition={{ duration: 2, repeat: isCurrent ? Infinity : 0, ease: "easeInOut" }}
                                style={{
                                    height: isCurrent ? 4 : isUpcoming ? 3 : 2,
                                    background: isCurrent
                                        ? `linear-gradient(90deg, ${tokens.color.ember}, #FFF6D8, ${tokens.color.ember})`
                                        : isUpcoming
                                            ? tokens.color.brass
                                            : isPast
                                                ? tokens.color.steel
                                                : tokens.color.parchment + "55",
                                    boxShadow: isCurrent
                                        ? `0 0 10px ${tokens.color.ember}, 0 0 3px #FFF6D8`
                                        : isUpcoming
                                            ? `0 0 5px ${tokens.color.brass}99`
                                            : "none",
                                }}
                            />
                            <div style={{ height: 10, marginTop: 1 }}>
                                {hasFlag && (
                                    <span
                                        className="text-tiny"
                                        style={{ color: tokens.color.burgundy, filter: `drop-shadow(0 0 3px ${tokens.color.burgundy}AA)` }}
                                        title="고정 이벤트"
                                    >
                                        🏁
                                    </span>
                                )}
                                {!hasFlag && isUpcoming && (
                                    <motion.span
                                        className="text-tiny"
                                        animate={{ opacity: [0.65, 1, 0.65] }}
                                        transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
                                        style={{ color: tokens.color.brass, display: "inline-block", filter: `drop-shadow(0 0 3px ${tokens.color.brass})` }}
                                        title={`${upcoming?.kind ?? "예고된 장면"} · ${upcoming?.title ?? ""}`}
                                    >
                                        ◆
                                    </motion.span>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
            {/* 다음 예고된 장면 카운트다운 칩 — 달력 우측에 붙여 "다음 뭐가 오는지" 를 한 눈에. */}
            {upcoming && upcomingDelta != null && (
                <div
                    className="ml-xs flex items-center gap-xs rounded-pill px-s py-[2px]"
                    style={{
                        background: `${tokens.color.brass}12`,
                        border: `1px solid ${tokens.color.brass}33`,
                        boxShadow: `0 0 8px ${tokens.color.brass}18`,
                    }}
                    title={upcoming.summary}
                >
                    <span
                        className="font-display"
                        style={{
                            color: tokens.color.brass,
                            fontSize: 12.5,
                            letterSpacing: tokens.type.letterSpacing.wide,
                            textShadow: `${tokens.color.ink} 0px 2px 8px`,
                        }}
                    >
                        {countdownLabel(upcomingDelta)} · {upcoming.title}
                    </span>
                </div>
            )}
        </motion.div>
    );
}

function countdownLabel(delta: number): string {
    if (delta <= 0) return "이번 주";
    if (delta === 1) return "다음 주";
    return `${delta}주 뒤`;
}

/** kind 문자열을 짧은 이모지 글리프로 매핑. 서사 톤 유지용.
 *   2026-04-25: 친구 WIP — 아직 사용처가 없어 noUnusedLocals 가 걸려서 export 로 보존. */
export function kindGlyph(kind: string): string {
    if (kind.includes("교전") || kind.includes("전투")) return "⚔";
    if (kind.includes("장 전환") || kind.includes("전환")) return "📖";
    if (kind.includes("결단")) return "⚖";
    if (kind.includes("컷신") || kind.includes("추모")) return "🕯";
    if (kind.includes("탐사")) return "🗺";
    if (kind.includes("분기") || kind.includes("보급")) return "🪙";
    return "◆";
}
